import { action, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import stores from 'cd-eft/models/Stores';
import {
    WaterwayVertexParamDTO,
    WaterwaySizingResponseDTO,
    WaterwaySizingRequestReachDTO,
    AlignedElementDesignParamsRequestDTO,
    WaterwaySummaryResultDTO
} from "cd-eft/_codegen_/openapi-generator/eft-set";
import { WaterwayDesignToolsReachJsonController, WaterwayDesignToolsController } from 'cd-eft/services/eftSetApi';

import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapView from '@arcgis/core/views/MapView';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';

import { removeLayers, putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import { LinearUnit, constructWaterwayLayer } from 'cd-eft/utils/structureLayerUtils';
import { toTitleFromUnderscore } from "cd-eft/utils/stringUtilities";
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

class WaterwayStoreProjectsStore extends ProjectsStore {
    constructor(private waterwaydesignstore: any) {
        super(waterwaydesignstore);
    }
}

interface IWaterwayDesignObject {
    beginningX: number;
    beginningY: number;
    channelShapeType: string;
    startGradeLength: number;
    endGradeLength: number;
    startGuardrail: number;
    endGuardrail: number;
    peakFlow: number;
    waterwayDesignStations: IWaterwayDesignStation[];
    alignedElementDesignParametersId: number;
    editingStatus: string;
    surfaceDefId: number;
    surfaceDefTitle: string;
    sessionId: number;
    disableCalculateButtonDesignPage: boolean,
    DisableSaveButtonDesignPage: boolean,
    hasWaterwayCalculations: boolean
}
interface ISvcProps {
    slope?: number;
    totalQ?: number;
    leftSS?: number;
    rightSS?: number;
    bottomWidth?: number;
    soilStrengthName?: string;
    soilStrengthValue?: number;
    vegCover?: number;
    vegCoverName?: string;
    stability?: string;
    capacity?: string;
    offset1?: number;
    offset2?: number;
    offset3?: number;
    freeBoard?: number;
}

//IWaterwayVertexParamDTO is based on BE WaterwayVertexParamDTO (to have all the fields) when we call addWDTEdits() which is used to prepare payload for the /saveReachEdits api
export interface IWaterwayVertexParamDTO {
    addedQ: number;
    capacity: string;
    channelElev: number;
    channelParams: IChannelParams
    channelShapeType: string;
    cutFill: number;
    depth: number;
    depthEdited: boolean;
    endStation: string;
    errorCode: string;
    flowQEdited: boolean;
    grade: number;
    groundElev: number;
    reachId: string;
    simDepth: number;
    simFlowQ: number;
    simWidth: number;
    soilStrength: string;
    stability: string;
    startStation: string;
    stationDist: number;
    status: string;
    totalQ: number;
    vegetalCover: string;
    width: number;
    widthEdited: boolean;
}

interface IChannelParams {
    startSlopeDistance: number,
    bermWidth: number,
    leftSlopeRatio: number,
    dipDepth: number,
    offset2: number,
    sideWidthLimit: number,
    offset1: number,
    backSlopeRatio: number,
    sideSlope: number,
    freeBoard: number,
    offset3: number,
    rightSlopeRatio: number,
    minBedWidth: number,
    endSlopeDistance: number
}

//Inital object for the Waterway Design (Waterway Profile/Reaches) table
export interface IWaterwayDesignStation {
    status: string;
    reachId: string;
    startStation: string;
    endStation: string;
    station: string;
    groundZ: number;
    channelZ: number;
    cutFill: number;
    stationLength: number;
    grade: number;
    leftSS: number;
    rightSS: number;
    bottomWidth: number;
    offset1: number;
    offset2: number;
    offset3: number;
    runoffQ;
    addedQ;
    totalQ;
    flowDepthFB: number;
    flowTopWidth: number;
    soilStrength: string;
    vegCover: string;
    stability: string;
    capacity: string;
    channelParams: IChannelParams;
    channelShapeType: string; //~ channelControl
    error: string;
    depthEdited: boolean;
    widthEdited: boolean;
    simDepth?: number;
    depth: number;
    stabVelocity: number;
    capaVelocity: number;
}

interface IProfileSettings {
    bankProfile: string;
    fineGrid: number;
    lowBankOffset: number;
    sampleInterval: number;
    showCoord: boolean;
    verticalInterval: number;
}
interface IWDTGroupTracker {
    groupCount: number,
    currentObject: IWaterwayDesignObject
}

export const convertStationBasedOnWaterwayVertexParamDTO = (station, channelType) => {
    const channelParams = ((station.reachId === "START" || station.reachId === "END") && !station.channelParams) ? {
        startSlopeDistance: 0,
        bermWidth: 0,
        leftSlopeRatio: 0,
        dipDepth: 0,
        offset2: 0,
        sideWidthLimit: 0,
        offset1: 0,
        backSlopeRatio: 0,
        sideSlope: 0,
        freeBoard: 0,
        offset3: 0,
        rightSlopeRatio: 0,
        minBedWidth: 0,
        endSlopeDistance: 0,
    }
        : {
            startSlopeDistance: parseFloat(station.channelParams.startSlopeDistance),
            bermWidth: parseFloat(station.channelParams.bermWidth),
            leftSlopeRatio: parseFloat(station.leftSS),
            dipDepth: parseFloat(station.channelParams.dipDepth),
            offset2: parseFloat(station.offset2),
            sideWidthLimit: parseFloat(station.channelParams.sideWidthLimit),
            offset1: parseFloat(station.offset1),
            backSlopeRatio: parseFloat(station.channelParams.backSlopeRatio),
            sideSlope: parseFloat(station.channelParams.sideSlope),
            freeBoard: parseFloat(station.channelParams.freeBoard),
            offset3: parseFloat(station.offset3),
            rightSlopeRatio: parseFloat(station.rightSS),
            minBedWidth: parseFloat(station.bottomWidth),
            endSlopeDistance: parseFloat(station.channelParams.endSlopeDistance),
        }
    if (station.reachId === 'START' || station.reachId === 'END') {
        station.grade = station.grade !== undefined ? parseFloat(parseFloat(station.grade).toFixed(2)) : station.grade;
    }
    const stationDTO: IWaterwayVertexParamDTO = {
        addedQ: parseFloat(station.addedQ),
        capacity: station.capacity,
        channelElev: parseFloat(station.channelZ),
        channelParams: channelParams,
        channelShapeType: channelType,
        cutFill: parseFloat(station.cutFill),
        depth: parseFloat(station.depth),
        depthEdited: station.depthEdited,
        endStation: station.endStation,
        errorCode: station.error,
        flowQEdited: station.flowQEdited,
        grade: station.grade === "-" ? 0.0 : station.grade,
        groundElev: parseFloat(station.groundZ),
        reachId: station.reachId,
        simDepth: parseFloat(station.simDepth),
        simFlowQ: parseFloat(station.simFlowQ),
        simWidth: parseFloat(station.simWidth),
        soilStrength: station.soilStrength,
        stability: station.stability,
        startStation: station.startStation === "-" ? null : station.startStation,
        stationDist: parseFloat(station.stationLength),
        status: station.status,
        totalQ: parseFloat(station.totalQ),
        vegetalCover: station.vegCover,
        width: parseFloat(station.width),
        widthEdited: station.widthEdited,
    }
    return stationDTO;
}
type WaterwayStep = 'waterway-input' | 'design-waterway' | 'results';
export class WaterwayDesignStore {
    constructor(private appStore: any) {
        this.projectsStore = new WaterwayStoreProjectsStore(this);
        this.init();
    }
    @observable requiredInputsValidated: boolean = false;
    @observable projectsStore: WaterwayStoreProjectsStore;
    @observable waterwayDesignObject!: IWaterwayDesignObject;
    @observable waterwayStationsEditingClone;
    @observable waterwayDesignStation!: IWaterwayDesignStation;
    @observable selectedWaterwayStations: any[] = [];
    @observable selectedHydroProjectName; string = "";
    @observable globalFreeBoard = 0;
    @observable currentElement: any = {};
    @observable showWSCMiniComp: boolean = false
    @observable enableExpand: boolean = false;
    @observable svcProps: ISvcProps = {};
    @observable uiState: string = ""
    @observable tableView: string = '';
    @observable loadingComplete: boolean = true;
    @observable deleteStationFromTable: boolean = false;
    @observable waterwaySummaryBannerReportObj: WaterwaySummaryResultDTO | undefined = undefined;
    @observable selectedWaterwayStationForWsc: string | null = null;
    // this parameter changesOnInput is to serve not to call the function getAllReachesByAlignedElementDesignParametersId() when the waterwayDesign component is rendered if the input page has changed
    // But now Jeff has implemented the corresponding changes on design page, so this parameter lost its function. 
    // @observable changesOnInput: boolean = false;// If Input was saved, set to true. made false if design saved.

    @observable alignedDesignElementId: number = 0;
    @observable alignedFeatureName: string = '';
    @observable savedPayload?: AlignedElementDesignParamsRequestDTO;
    @observable waterwaySizingCalculatorResults: WaterwaySizingResponseDTO | undefined = undefined;
    // Design Controls section:
    @observable sideFillSlope: number = 10;
    @observable bermTopWidth: number = 4;
    @observable sideWidthLimit: number = 30;
    @observable bermBackSlope: number = 4;
    @observable channelControl: 'TRAPEZOIDAL' | 'PARABOLIC' | '' = '';
    @observable startGradeLength: number = 0;
    @observable endGradeLength: number = 0;
    @observable startGradeSlope: number = 0;
    @observable endGradeSlope: number = 0;
    @observable initialQFraction: number = 100;
    @observable channelEndControl: 0 | 1 = 0; // 0--> Channel End Length, 1-->Channel End Slope
    @observable updateStartEndLength: boolean = false;
    @observable wdtGroupsOriginals: IWDTGroupTracker[] = []; // Used to save pre-PEM WDT groups

    // alignment section 
    @observable alignmentId: number = 0;
    @observable seedingBufferWidth: number = 0;
    @observable seedingBufferOption: number = 1;


    @observable currentWorkflowStep: WaterwayStep = 'waterway-input';
    @observable waterwayInputsPageInputsChange: boolean = false;
    @observable renderWaterwayDesign: boolean = false;
    @observable waterwayLayers: GroupLayer[] = [];
    @observable checkAlignmentList: boolean = false;
    @observable threeCrossSectionReachID: string = '';
    @observable threeCrossSectionStation: string = '';
    @observable threeCrossSectionTableViewToggle = false;
    // profile settings in waterwayDesign : default values
    @observable profileSettings: IProfileSettings = {
        bankProfile: 'showMergedLowBank',
        fineGrid: 25,
        lowBankOffset: 20,
        sampleInterval: 2,
        showCoord: false,
        verticalInterval: 5
    }

    // change flags -- need to persist until cleared in Save & Calculate button
    @observable aligmentChange: number = 0;
    @observable hydrologyChange: number = 0;
    @observable designControlChange: number = 0;

    // Loading and Errors -->
    @observable notifications =
        {
            unsavedEdits: '',
            unsavedEditsClosePage: ''
        }
    @observable isLoading: boolean = false;
    @observable error: boolean = false;
    @observable errorMsg: string = '';
    @observable originalFlowDepthFBObject: any[] = [];
    @observable endStation_XCenterLine: number = 0;
    @observable channelStartGradeLengthChangedGlobal: boolean = false;
    @observable channelEndGradeLengthChangedGlobal: boolean = false;
    @observable stationLengthEdited = false;
    @observable gradeEdited = false;
    @observable startGradeNegative = false;
    @observable endGradeNegative = false;
    @observable WSCselectedReachNames: string[] = [];
    @observable WSCreachNames: string = '';
    @observable waterwayName: string = '';
    @observable waterwayType: string = '';
    @observable saveWaterwayTypePage: boolean = false;
    @observable openedFromWarningIcon: boolean = false;
    @observable wscComponentRef: any = null;
    @observable WSCstatusErrorCode: string = "";
    @observable WSCstatusErrorMessage: string = "";
    @observable WSCstatus: string = "";
    @observable hasChannelDimensionsError: boolean = false;

    @observable defaultLiningMaterial: string = '';
    // defaultLiningMaterialChanged index 0: input page, index 1: design page
    // the way they change back to false from true is different. 
    @observable defaultLiningMaterialChanged: boolean[] = [false, false];

    @observable currentWaterwayDesignElementTypeId: number = 0;
    @observable summaryWaterwayTypes: any[] = []; // Don't include in reset function
    @action.bound setError(msg: string) {
        if (msg !== '') {
            this.error = true;
            this.errorMsg = msg;
            this.isLoading = false;
        } else {
            this.error = false;
            this.errorMsg = '';
            this.isLoading = false;
        }
    }
    // <-- Loading and Errors

    @action.bound reset() {
        this.init();
    }

    @action.bound displayWscComponent() {
        this.showWSCMiniComp = true;
    }

    @action.bound closeWscComponent() {
        this.showWSCMiniComp = false;
        this.clearWscSelection();
    }

    @action resetCurrentObject() {
        this.selectedWaterwayStations = [];
        this.currentElement = { element: '', name: '', id: '', type: '' };
        this.waterwayDesignObject = { ...this.initialWaterwayDesignObject };
    }

    @action init() {
        this.resetCurrentObject();
        this.setError('');
        this.isLoading = false;
        // this.profileSettings = this.initialProfileSettings;
        this.alignedDesignElementId = 0;
        this.alignedFeatureName = '';
        this.savedPayload = undefined;
        this.sideFillSlope = 10;
        this.bermTopWidth = 4;
        this.sideWidthLimit = 30;
        this.bermBackSlope = 4;
        this.channelControl = '';
        this.startGradeLength = 0;
        this.endGradeLength = 0;
        this.startGradeSlope = 0;
        this.endGradeSlope = 0;
        this.initialQFraction = 100;
        this.channelEndControl = 0; // 0--> Channel End Length, 1-->Channel End Slope
        this.updateStartEndLength = false;
        this.channelStartGradeLengthChangedGlobal = false;
        this.channelEndGradeLengthChangedGlobal = false;
        this.wdtGroupsOriginals = [];
        this.waterwayName = '';
        this.waterwayType = '';
        this.currentWaterwayDesignElementTypeId = 0;
        this.defaultLiningMaterial = '';
        this.defaultLiningMaterialChanged = [false, false];
        this.threeCrossSectionReachID = '';
        this.uiState = '';
    }

    initialWaterwayDesignStation: IWaterwayDesignStation = {
        status: '',
        reachId: '',
        startStation: '',
        endStation: '',
        station: '',
        groundZ: 0.0,
        channelZ: 0.0,
        cutFill: 0.0,
        stationLength: 0.0,
        depth: 0,
        grade: 0.0,
        leftSS: 0,
        rightSS: 0,
        bottomWidth: 0,
        offset1: 0,
        offset2: 0,
        offset3: 0,
        runoffQ: 0.0,
        addedQ: 0.0,
        totalQ: 0.0,
        flowDepthFB: 0.0,
        flowTopWidth: 0.0,
        depthEdited: false,
        widthEdited: false,
        soilStrength: '',
        vegCover: '',
        stability: '',
        capacity: '',
        stabVelocity: 0,
        capaVelocity: 0,
        channelParams: {
            startSlopeDistance: 0.0,
            bermWidth: 0.0,
            leftSlopeRatio: 0.0,
            dipDepth: 0.0,
            offset2: 0.0,
            sideWidthLimit: 0.0,
            offset1: 0.0,
            backSlopeRatio: 0.0,
            sideSlope: 0.0,
            freeBoard: 0.0,
            offset3: 0.0,
            rightSlopeRatio: 0.0,
            minBedWidth: 0.0,
            endSlopeDistance: 0.0,
        },
        channelShapeType: '', //~ channelControl
        error: ''
    };

    initialWaterwayDesignObject: IWaterwayDesignObject = {
        beginningX: 0,
        beginningY: 0,
        channelShapeType: this.channelControl,
        startGradeLength: 0,
        endGradeLength: 0,
        startGuardrail: 0,
        endGuardrail: 0,
        peakFlow: 0,
        waterwayDesignStations: [],
        alignedElementDesignParametersId: 0,
        editingStatus: '',
        surfaceDefId: 0,
        surfaceDefTitle: '',
        sessionId: 0,
        disableCalculateButtonDesignPage: false, // control generate surface button in waterway design 
        DisableSaveButtonDesignPage: false, // control save&calculate button in waterway design 
        hasWaterwayCalculations: false, // control generate surface button in waterway design
    };

    initialProfileSettings: IProfileSettings = {
        bankProfile: 'showMergedLowBank',
        fineGrid: 25,
        lowBankOffset: 20,
        sampleInterval: 2,
        showCoord: false,
        verticalInterval: 5
    }



    @action.bound saveInputPayloadAndId(requestPayload, responseId, name, channelControl, startGradeLength, endGradeLength, peakFlow, sideFillSlope, sideWidthLimit, bermTopWidth, bermBackSlope, endGradeSlope, startGradeSlope, initialQFraction, channelEndControl, alignmentId, seedingBufferWidth, freeBoard, defaultLiningMaterial) {
        this.alignedDesignElementId = responseId;
        this.alignedFeatureName = name;
        this.savedPayload = requestPayload;
        this.channelControl = channelControl;
        this.startGradeLength = startGradeLength;
        this.endGradeLength = endGradeLength;
        this.waterwayDesignObject.startGradeLength = startGradeLength;
        this.waterwayDesignObject.endGradeLength = endGradeLength;
        this.waterwayDesignObject.peakFlow = peakFlow;
        this.sideFillSlope = sideFillSlope;
        this.sideWidthLimit = sideWidthLimit;
        this.bermTopWidth = bermTopWidth;
        this.bermBackSlope = bermBackSlope;
        this.endGradeSlope = endGradeSlope;
        this.startGradeSlope = startGradeSlope;
        this.initialQFraction = initialQFraction;
        this.channelEndControl = channelEndControl;
        this.alignmentId = alignmentId;
        this.seedingBufferWidth = seedingBufferWidth;
        this.svcProps.freeBoard = freeBoard;
        this.defaultLiningMaterial = defaultLiningMaterial;
    }

    @action.bound isStationSelectedForWsc(reachId: string): boolean {
        return this.selectedWaterwayStationForWsc === reachId;
    }
    @action.bound selectReachForWSC(reachId: string) {
        this.selectedWaterwayStationForWsc = reachId;

        if (!this.openedFromWarningIcon) {
            this.WSCstatusErrorCode = "";
            this.WSCstatusErrorMessage = "";
        }
    }
    @action.bound clearWscSelection() {
        this.selectedWaterwayStations = [];
        this.selectedWaterwayStationForWsc = null;
    }
    @action.bound updateSelectedWaterwayStations(stations: string[]) {
        this.selectedWaterwayStations = [...stations];
    }

    @action.bound async conversionBetweenSlopeAndDistance(alignedElementDesignParametersId, channelEndsControlType, endSlopeDistance, endSlopePercentage, startSlopeDistance, startSlopePercentage) {
        const response = await WaterwayDesignToolsController.updateWaterwayDesignControlSettingsUsingPOST(
            { alignedElementDesignParametersId, channelEndsControlType, endSlopeDistance, endSlopePercentage, startSlopeDistance, startSlopePercentage, designControlSyncDisabled: true }
        )
        if (response && response.data) {
            const result = response.data;
            this.endGradeLength = Number(result.endSlopeDistance!.toFixed(2));
            this.endGradeSlope = Number(result.endSlopePercentage!.toFixed(2));
            this.startGradeLength = Number(result.startSlopeDistance!.toFixed(2));
            this.startGradeSlope = Number(result.startSlopePercentage!.toFixed(2));
        }
    }

    get exportWaterwaySizingRequestReachDTO() {
        if (this.selectedWaterwayStations && this.waterwaySizingCalculatorResults && this.selectedWaterwayStations.length >= 1) {
            const exportResult: WaterwaySizingRequestReachDTO = {
                alignedElementDesignParametersId: this.alignedDesignElementId,
                reachIds: this.selectedWaterwayStations,
                waterWaySizingResponseParamDto: this.waterwaySizingCalculatorResults
            }
            return exportResult;
        }
        return undefined;
    }

    @action.bound exportWaterwayReachEditAdapter(): WaterwayVertexParamDTO[] {
        const selectedWaterwayDesignStations = this.waterwayDesignObject.waterwayDesignStations.filter(e => this.selectedWaterwayStations.includes(e.reachId));
        const result = selectedWaterwayDesignStations.map(station => {
            return {
                addedQ: station.addedQ,
                capacity: station.capacity,
                channelElev: station.channelZ,
                channelParams: {
                    rightSlopeRatio: station.channelParams.rightSlopeRatio,
                    leftSlopeRatio: station.channelParams.leftSlopeRatio,
                    minBedWidth: station.channelParams.minBedWidth,
                    offset1: station.channelParams.offset1,
                    offset2: station.channelParams.offset2,
                    offset3: station.channelParams.offset3,
                    freeBoard: station.channelParams.freeBoard,
                    startSlopeDistance: station.channelParams.startSlopeDistance,
                    bermWidth: station.channelParams.bermWidth,
                    dipDepth: station.channelParams.dipDepth,
                    sideWidthLimit: station.channelParams.sideWidthLimit,
                    backSlopeRatio: station.channelParams.backSlopeRatio,
                    sideSlope: station.channelParams.sideSlope,
                    endSlopeDistance: station.channelParams.endSlopeDistance
                },
                channelShapeType: stores.AppStore.waterwayDesignStore.channelControl,
                cutFill: station.cutFill,
                depthEdited: station.depthEdited,
                widthEdited: station.widthEdited,
                endStation: station.endStation,
                flowQEdited: false,
                grade: station.grade,
                groundElev: station.groundZ,
                reachId: station.reachId,
                simDepth: !station.depthEdited ? station.flowDepthFB : undefined,
                simFlowQ: station.runoffQ,
                simWidth: !station.widthEdited ? station.flowTopWidth : undefined,
                width: station.widthEdited ? station.flowTopWidth : undefined,
                depth: station.depthEdited ? station.flowDepthFB : undefined,
                soilStrength: station.soilStrength,
                stability: station.stability,
                startStation: station.startStation,
                stationDist: station.stationLength,
                status: station.status,
                totalQ: station.totalQ,
                vegetalCover: station.vegCover,
            }
        })
        return result;

    }

    @action.bound updatedReachTableAfterAPISuccess() {
        if (this.waterwaySizingCalculatorResults) {
            // Trapezoid
            const results = this.waterwaySizingCalculatorResults;
            const bottomWidth = results.channelParams?.minBedWidth;
            const rightSS = results.channelParams?.rightSlopeRatio;
            // Parabola
            const offset1 = results.channelParams?.offset1;
            const offset2 = results.channelParams?.offset2;
            const offset3 = results.channelParams?.offset3;
            // Common
            const leftSS = results.channelParams?.leftSlopeRatio;
            const flowDepthFB = results.dataVertex?.simDepth;
            const flowTopWidth = results.dataVertex?.simWidth;
            const soilStrength = results.soilParameters?.inputModeStr === "Erodibility Class" ?
                `${results.soilParameters?.allowableStress} (${toTitleFromUnderscore(results.soilParameters?.stressFactorStr)})` :
                `${results.soilParameters?.allowableStress}`;
            const vegCover = results.vcffactor!.toString() + ` (${toTitleFromUnderscore(results.vcDescription)})`;
            const stability = results.vegetalCapacityOption === 'Retardance Class' ? `${results.vegetalStabilityParams?.rci} (${results.vegetalStabilityParams?.retClassStr})` : `${results.vegetalStabilityParams?.rci}`;
            const capacity = results.vegetalCapacityOption === 'Retardance Class' ? `${results.vegetalCapacityParams?.rci} (${results.vegetalCapacityParams?.retClassStr})` : `${results.vegetalCapacityParams?.rci}`;
            this.selectedWaterwayStations.forEach((reachID) => {
                this.waterwayDesignObject.waterwayDesignStations.forEach((p) => {
                    const resetDepth = p.depthEdited && !results.dataVertex!.depthEdited;
                    const resetWidth = p.widthEdited && !results.dataVertex!.widthEdited;
                    if (p.reachId === reachID) {
                        // p.grade = results.channelSlope as number;
                        p.totalQ = results.dataVertex?.totalQ;
                        p.leftSS = leftSS!;
                        p.bottomWidth = bottomWidth!;
                        p.offset1 = offset1! * 100;
                        p.offset2 = offset2! * 100;
                        p.offset3 = offset3! * 100;
                        p.rightSS = rightSS!;
                        p.flowDepthFB = resetDepth ? +'0.0' : +flowDepthFB!;
                        p.depthEdited = results.dataVertex!.depthEdited!;
                        p.flowTopWidth = resetWidth ? +'0.0' : +flowTopWidth!;
                        p.widthEdited = results.dataVertex!.widthEdited!;
                        p.soilStrength = soilStrength.toString();
                        p.vegCover = vegCover.toString();
                        p.stability = stability.toString();
                        p.capacity = capacity.toString();
                        p.channelParams.leftSlopeRatio = leftSS!;
                        p.channelParams.offset1 = offset1! * 100;
                        p.channelParams.offset2 = offset2! * 100;
                        p.channelParams.offset3 = offset3! * 100;
                        p.channelParams.freeBoard = results.channelParams!.freeBoard!;
                        p.channelParams.rightSlopeRatio = rightSS!;
                        p.channelParams.minBedWidth = bottomWidth!;
                        return p
                    }
                    else return p
                })
            })

        }
    }

    @action.bound async saveWaterwaySizingResponseDtoReaches(callBack) {
        const exportResult: WaterwaySizingRequestReachDTO | undefined = this.exportWaterwaySizingRequestReachDTO;
        if (exportResult) {
            try {
                const response: any = await WaterwayDesignToolsReachJsonController.saveWaterwaySizingResponseDtoReachesUsingPOST(exportResult);
                if (response && response.data) {
                    this.updatedReachTableAfterAPISuccess();
                    const exportWaterwayReachEditAdapter = this.exportWaterwayReachEditAdapter();
                    exportWaterwayReachEditAdapter.forEach((e, index) => {
                        const lastEdit = index === exportWaterwayReachEditAdapter.length - 1 ? true : false;
                        callBack(new Map(Object.entries({ [this.selectedWaterwayStations.find((station) => station === e.reachId)]: e })), true, lastEdit)
                    })
                    this.closeWscComponent();
                }
            } catch (err) {
                console.error('error: saveWaterwaySizingResponseDtoReaches() exportResult is undefined or some other errors', err);
            }
        }
    }

    @action addWaterwayToMap(waterway: any, mapView: MapView, id: string, title?: string, fromRefresh?: boolean): GroupLayer {
        const groupLayerId = `Waterway${id}`;
        if (!fromRefresh) {
            removeLayers(this.waterwayLayers, mapView);
            this.waterwayLayers.forEach(layer => layer.destroy());
            this.waterwayLayers = [];
        }

        const newLayer = this.createWaterwayLayer(waterway, mapView, groupLayerId, id, title);
        mapView.map.add(newLayer);
        putStructureRepOnTop(mapView, groupLayerId);
        putStructureRepOnTop(mapView, 'alignment_sketch');
        this.waterwayLayers.push(newLayer);
        return newLayer;
    }

    private createWaterwayLayer(waterway: any, mapView: MapView, groupLayerId: string, id: string, title?: string): GroupLayer {
        const inputSR: SpatialReference = stores.AppStore.projectsLibraryStore.projectSpatialReference!;
        const outputSR: SpatialReference = mapView.spatialReference;
        const inputUnit: LinearUnit = 'intl-feet';
        const outputUnit: LinearUnit = 'meters';
        const waterwayLayer = constructWaterwayLayer(waterway, inputSR, outputSR, inputUnit, outputUnit, this.seedingBufferOption, groupLayerId, id, title);
        return waterwayLayer;
    }

    private getFilteredSelectedStations(): string[] {
        return this.selectedWaterwayStations.filter(
            station => station !== 'R-0' && station !== 'START' && station !== 'END'
        );
    }

    @action updateSelectedReachNames() {
        const filteredStations = this.getFilteredSelectedStations();
        this.WSCselectedReachNames = [...filteredStations];
        this.WSCreachNames = this.WSCselectedReachNames.join(', ');
    }

    @action.bound async defaultLiningMaterialChange() {
        if (this.currentWaterwayDesignElementTypeId !== 19 && this.waterwayType !== 'Lined Waterway') return;

        this.defaultLiningMaterialChanged[0] = false;

        const checkAllReaches = this.areCapacitiesSame(this.waterwayDesignObject.waterwayDesignStations);
        if (!checkAllReaches) {
            this.defaultLiningMaterialChanged[1] = false;
            return;
        }

        const defaultLiningMaterial = this.defaultLiningMaterial;
        const waterwayMaterial = this.waterwayDesignObject.waterwayDesignStations[1].capacity;

        if (defaultLiningMaterial === waterwayMaterial) {
            this.defaultLiningMaterialChanged[1] = false;
            return;
        }

        try {
            const response = await WaterwayDesignToolsController.saveLiningMaterialByAlignedDesignElementIDUsingPOST({
                alignedElementDesignParamsId: this.alignedDesignElementId,
                waterwayMaterial: waterwayMaterial
            });

            if (response.data === 'SUCCESS') {
                MessageCenter.addNotificationFromCode('WDT_155');
            }

            this.defaultLiningMaterial = waterwayMaterial;
            this.defaultLiningMaterialChanged[1] = true;
        } catch (error) {
            console.error("Error updating lining material:", error);
        }
    }

    areCapacitiesSame(array) {
        if (array.length <= 2) return true;
        const firstCapacity = array[1].capacity;
        return array.slice(1, -1).every(item => item.capacity === firstCapacity);
    }

    // ai design experimental code
    /* istanbul ignore next */
    clearWaterwayLayers(mapView: MapView) {
        removeLayers(this.waterwayLayers, mapView);
        this.waterwayLayers.forEach(layer => layer.destroy());
        this.waterwayLayers = [];
    }
    // End: ai design experimental code

    // @action handleReachSelectionChange(reachId: string, isSelected: boolean) {
    //     if (isSelected) {
    //         if (!this.WSCselectedReachNames.includes(reachId)) {
    //             this.WSCselectedReachNames.push(reachId);
    //         }
    //     } else if (reachId !== this.selectedWaterwayStationForWsc) {
    //         // Only remove the reach if it's not the initially selected one
    //         this.WSCselectedReachNames = this.WSCselectedReachNames.filter(id => id !== reachId);
    //     }
    //     this.WSCreachNames = this.WSCselectedReachNames.join(', ');
    // }

    // @action updateLiningMaterial(material: string) {
    //     this.liningMaterial = material;
    //     this.waterwayDesignObject.waterwayDesignStations.forEach(station => {
    //         if (station.reachId !== 'START' && station.reachId !== 'END') {
    //             station.capacity = material;
    //             station.stability = material;
    //         }
    //     });
    // }


    // @action updateLiningMaterialForReaches(reachIds: string[], newMaterial: string) {
    //     reachIds.forEach(reachId => {
    //         this.temporaryLiningMaterialUpdates.set(reachId, newMaterial);
    //     });
    // }

    // @action getLiningMaterialForReach(reachId: string): string {
    //     // First, check if there's a temporary update for this reach
    //     const temporaryMaterial = this.temporaryLiningMaterialUpdates.get(reachId);
    //     if (temporaryMaterial) {
    //       return temporaryMaterial;
    //     }
    //     return this.liningMaterial || '';
    //   }


    //   @action applyPendingLiningMaterialChange() {
    //     if (this.pendingLiningMaterialChange) {
    //         const editedStations = new Map();
    //         this.waterwayDesignObject.waterwayDesignStations
    //             .filter(station => station.reachId !== 'START' && station.reachId !== 'END' && station.reachId !== 'R-0')
    //             .forEach(station => {
    //                 station.capacity = this.pendingLiningMaterialChange as string;
    //                 station.stability = this.pendingLiningMaterialChange as string;
    //                 editedStations.set(station.reachId, convertStationBasedOnWaterwayVertexParamDTO(station, this.channelControl));
    //             });       
    //         this.pendingLiningMaterialChange = null;
    //     }
    // }

}
